import React, { useState, useEffect } from "react";
import ReactDOM from "react-dom";

interface DatePickerProps {
    isOpen: boolean;
    onClose: () => void;
    departureDate: string;
    returnDate: string;
    onDatesChange: (departure: string, returnDate: string) => void;
    tripType: string;
    onTripTypeChange: (value: string) => void;
    payWithPoints: boolean;
    onPayWithPointsChange: (value: boolean) => void;
}

const DatePicker: React.FC<DatePickerProps> = ({
    isOpen,
    onClose,
    departureDate,
    returnDate,
    onDatesChange,
    tripType,
    onTripTypeChange,
    payWithPoints,
    onPayWithPointsChange,
}) => {
    const [selectedDeparture, setSelectedDeparture] = useState<Date | null>(
        null
    );
    const [selectedReturn, setSelectedReturn] = useState<Date | null>(null);
    const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
    const [outboundInputValue, setOutboundInputValue] = useState("");
    const [returnInputValue, setReturnInputValue] = useState("");
    const [outboundFocused, setOutboundFocused] = useState(false);
    const [returnFocused, setReturnFocused] = useState(false);
    const [hoveredMonthIndex, setHoveredMonthIndex] = useState<number | null>(
        null
    );
    const [slideDirection, setSlideDirection] = useState<
        "left" | "right" | null
    >(null);

    // Generate 13 months starting from current month (with current month repeated at end)
    const generateMonthsData = () => {
        const months = [];
        const today = new Date();
        const currentMonth = today.getMonth();
        const currentYear = today.getFullYear();

        for (let i = 0; i < 13; i++) {
            const monthDate = new Date(currentYear, currentMonth + i, 1);
            const monthAbbr = monthDate.toLocaleDateString("en-US", {
                month: "short",
            });
            months.push({
                abbr: monthAbbr,
                index: i,
                date: monthDate,
            });
        }
        return months;
    };

    const monthsData = generateMonthsData();

    useEffect(() => {
        if (departureDate) {
            // Parse YYYY-MM-DD format in local timezone
            const [year, month, day] = departureDate.split("-").map(Number);
            const date = new Date(year, month - 1, day, 0, 0, 0, 0);
            setSelectedDeparture(date);
            setOutboundInputValue(departureDate);
        }
        if (returnDate && tripType !== "One way") {
            // Parse YYYY-MM-DD format in local timezone
            const [year, month, day] = returnDate.split("-").map(Number);
            const date = new Date(year, month - 1, day, 0, 0, 0, 0);
            setSelectedReturn(date);
            setReturnInputValue(returnDate);
        }
    }, [departureDate, returnDate, tripType]);

    useEffect(() => {
        if (isOpen) {
            // Start with the current month
            setCurrentMonthIndex(0);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const getMonthYear = (offset: number) => {
        const today = new Date();
        const month = new Date(
            today.getFullYear(),
            today.getMonth() + currentMonthIndex + offset,
            1
        );
        return month;
    };

    const getDaysInMonth = (date: Date) => {
        return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
    };

    const getFirstDayOfMonth = (date: Date) => {
        const day = new Date(date.getFullYear(), date.getMonth(), 1).getDay();
        // Convert Sunday (0) to 7, so Monday becomes 0
        return day === 0 ? 6 : day - 1;
    };

    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    const handleDateClick = (day: number, month: Date) => {
        const clickedDate = new Date(
            month.getFullYear(),
            month.getMonth(),
            day
        );

        if (
            !selectedDeparture ||
            (selectedDeparture && selectedReturn) ||
            tripType === "One way"
        ) {
            setSelectedDeparture(clickedDate);
            setSelectedReturn(null);
            const formattedDate = formatDate(clickedDate);
            setOutboundInputValue(formattedDate);
            setReturnInputValue("");
            // Update query parameters immediately
            onDatesChange(formattedDate, "");
        } else if (!selectedReturn) {
            if (clickedDate < selectedDeparture) {
                // Swap dates if clicked date is before departure
                const newDeparture = formatDate(clickedDate);
                const newReturn = formatDate(selectedDeparture);
                setSelectedReturn(selectedDeparture);
                setSelectedDeparture(clickedDate);
                setOutboundInputValue(newDeparture);
                setReturnInputValue(newReturn);
                // Update query parameters immediately
                onDatesChange(newDeparture, newReturn);
            } else {
                const returnDate = formatDate(clickedDate);
                setSelectedReturn(clickedDate);
                setReturnInputValue(returnDate);
                // Update query parameters immediately
                onDatesChange(formatDate(selectedDeparture), returnDate);
            }
        }
    };

    const handleOutboundInputChange = (value: string) => {
        setOutboundInputValue(value);
        // Try to parse the date if it's in the correct format
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split("-").map(Number);
            const date = new Date(year, month - 1, day, 0, 0, 0, 0);
            if (!isNaN(date.getTime())) {
                setSelectedDeparture(date);
                // Update query parameters immediately
                onDatesChange(value, returnInputValue);
            }
        }
    };

    const handleReturnInputChange = (value: string) => {
        setReturnInputValue(value);
        // Try to parse the date if it's in the correct format
        if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
            const [year, month, day] = value.split("-").map(Number);
            const date = new Date(year, month - 1, day, 0, 0, 0, 0);
            if (!isNaN(date.getTime())) {
                setSelectedReturn(date);
                // Update query parameters immediately
                onDatesChange(outboundInputValue, value);
            }
        }
    };

    const isDateInRange = (day: number, month: Date) => {
        if (!selectedDeparture || !selectedReturn) return false;
        const date = new Date(month.getFullYear(), month.getMonth(), day);
        return date >= selectedDeparture && date <= selectedReturn;
    };

    const handleDone = () => {
        // No need to update dates here since they're already updated on selection
        onClose();
    };

    const renderCalendar = (month: Date) => {
        const daysInMonth = getDaysInMonth(month);
        const firstDay = getFirstDayOfMonth(month);
        const days = [];
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        for (let i = 0; i < firstDay; i++) {
            days.push(<div key={`empty-${i}`} className="h-10"></div>);
        }

        for (let day = 1; day <= daysInMonth; day++) {
            const date = new Date(month.getFullYear(), month.getMonth(), day);
            date.setHours(0, 0, 0, 0);
            const isPast = date < today;
            const isInRange = isDateInRange(day, month);
            const isDeparture =
                selectedDeparture &&
                date.getTime() === selectedDeparture.getTime();
            const isReturn =
                selectedReturn && date.getTime() === selectedReturn.getTime();

            days.push(
                <button
                    key={day}
                    onClick={() => !isPast && handleDateClick(day, month)}
                    disabled={isPast}
                    className={`
                        h-10 w-full text-sm transition-colors rounded-lg
                        ${
                            isPast
                                ? "text-sas-gray-border cursor-not-allowed"
                                : !isDeparture && !isReturn && !isInRange
                                ? "hover:bg-gray-100"
                                : ""
                        }
                        ${
                            isDeparture || isReturn
                                ? "text-white"
                                : isInRange
                                ? "text-gray-900"
                                : "text-gray-700"
                        }
                    `}
                    style={{
                        backgroundColor:
                            isDeparture || isReturn
                                ? "#003df5"
                                : isInRange
                                ? "#c9e5f4"
                                : "transparent",
                    }}
                >
                    {day}
                </button>
            );
        }

        return days;
    };

    const calculateTripLength = () => {
        if (!selectedDeparture || !selectedReturn) return null;
        const diffTime = Math.abs(
            selectedReturn.getTime() - selectedDeparture.getTime()
        );
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const nights = diffDays;
        const days = diffDays + 1;
        return `${days} days, ${nights} nights`;
    };

    return ReactDOM.createPortal(
        <div
            className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center"
            style={{ zIndex: 9999 }}
            onClick={onClose}
        >
            <div
                className="bg-white rounded-2xl shadow-xl overflow-hidden flex flex-col"
                style={{
                    maxWidth: "1264px",
                    maxHeight: "640px",
                    width: "calc(100% - 40px)",
                    height: "calc(100vh - 38px)",
                    margin: "19px",
                }}
                onClick={(e) => e.stopPropagation()}
            >
                {/* Header */}
                <div className="bg-white px-0 pt-6 pb-6 border-b border-gray-200 mb-6 mx-6">
                    <div className="flex items-center justify-between">
                        {/* Tabs */}
                        <div className="relative">
                            <div className="flex items-center gap-0">
                                <button
                                    disabled
                                    className="pb-2 px-4 py-1 font-normal transition-all relative text-sas-text hover:bg-gray-100 hover:rounded-md cursor-default"
                                >
                                    Calendar
                                    <div className="absolute bottom-0 left-0 right-0 h-[4px] bg-[#00175c]"></div>
                                </button>
                                <button
                                    disabled
                                    className="pb-2 px-4 py-1 font-normal transition-all relative text-sas-text hover:bg-gray-100 hover:rounded-md cursor-default"
                                >
                                    Low price calendar
                                </button>
                                <button
                                    disabled
                                    className="pb-2 px-4 py-1 font-normal transition-all relative text-sas-text hover:bg-gray-100 hover:rounded-md cursor-default"
                                >
                                    Price chart
                                </button>
                            </div>
                            <div className="absolute bottom-0 left-0 right-0 h-[1px] bg-gray-500"></div>
                        </div>

                        {/* Date inputs */}
                        <div className="flex items-center gap-4">
                            <div
                                className="relative"
                                style={{ maxWidth: "140px", height: "60px" }}
                            >
                                <select
                                    value={tripType}
                                    onChange={(e) => {
                                        const newTripType = e.target.value;
                                        onTripTypeChange(newTripType);

                                        // Clear return date if switching to One way
                                        if (newTripType === "One way") {
                                            setSelectedReturn(null);
                                            setReturnInputValue("");
                                        }
                                    }}
                                    className="appearance-none bg-white border border-gray-300 rounded-lg px-4 h-full w-full pr-10 text-sas-text font-normal hover:bg-gray-50 hover:border-gray-600 focus:outline-none focus:border-sas-blue text-base transition-colors cursor-pointer"
                                >
                                    <option>Return trip</option>
                                    <option>One way</option>
                                    <option>Multi journey</option>
                                </select>
                                <div className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                                    <svg
                                        className="w-5 h-5 text-sas-text"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        {/* <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /> */}
                                    </svg>
                                </div>
                            </div>

                            <div
                                className="relative"
                                style={{ height: "60px", width: "161px" }}
                            >
                                <svg
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-black z-10"
                                >
                                    <path
                                        fill="currentColor"
                                        d="M19.75 20.5H4.25a.73.73 0 0 1-.534-.215.73.73 0 0 1-.216-.535q0-.32.216-.535A.73.73 0 0 1 4.25 19h15.5q.32 0 .535.215a.73.73 0 0 1 .215.535.73.73 0 0 1-.215.535.73.73 0 0 1-.535.215M5.473 12.871l4.944-1.338-3.61-6.112a.68.68 0 0 1-.06-.581.6.6 0 0 1 .43-.405l.329-.106a.57.57 0 0 1 .349-.02q.172.045.309.157l6.561 5.882 4.952-1.321q.685-.186 1.238.257.555.443.554 1.174 0 .473-.28.83-.28.358-.727.489L5.719 15.762a.82.82 0 0 1-.572-.026.88.88 0 0 1-.432-.386l-1.87-3.21a.44.44 0 0 1-.029-.445.48.48 0 0 1 .357-.278l.289-.056a.45.45 0 0 1 .217.003q.106.028.202.109z"
                                    ></path>
                                </svg>
                                <label
                                    className={`absolute left-14 transition-all pointer-events-none ${
                                        outboundFocused || outboundInputValue
                                            ? "top-2 text-sm text-gray-600"
                                            : "top-1/2 -translate-y-1/2 text-base text-gray-700"
                                    }`}
                                >
                                    Outbound
                                </label>
                                <input
                                    type="text"
                                    value={outboundInputValue}
                                    onChange={(e) =>
                                        handleOutboundInputChange(
                                            e.target.value
                                        )
                                    }
                                    onFocus={() => setOutboundFocused(true)}
                                    onBlur={() => setOutboundFocused(false)}
                                    className={`w-full h-full pl-11 pr-3 ${
                                        outboundFocused || outboundInputValue
                                            ? "pt-6 pb-2"
                                            : ""
                                    } border border-[#807e7e] rounded-lg text-sas-text focus:outline-none focus:border-sas-blue hover:bg-gray-50 hover:border-gray-600 transition-colors`}
                                />
                            </div>

                            {tripType !== "One way" && (
                                <div
                                    className="relative"
                                    style={{
                                        height: "60px",
                                        width: "161px",
                                    }}
                                >
                                    <svg
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        className="absolute left-3 top-1/2 -translate-y-1/2 w-6 h-6 text-black z-10"
                                    >
                                        <path
                                            fill="currentColor"
                                            d="m18.658 15.756-14.5-4.027a.94.94 0 0 1-.477-.321.85.85 0 0 1-.181-.542V7.25a.47.47 0 0 1 .2-.397.44.44 0 0 1 .433-.068l.279.086a.5.5 0 0 1 .323.314l.619 1.886 4.896 1.379V3.375q0-.34.26-.551a.61.61 0 0 1 .59-.116l.338.08a.6.6 0 0 1 .301.165.8.8 0 0 1 .19.3l2.629 8.382 4.884 1.37q.49.132.773.522.285.39.285.879 0 .699-.579 1.123a1.39 1.39 0 0 1-1.263.227M4.25 20.5a.73.73 0 0 1-.534-.215.73.73 0 0 1-.216-.535q0-.32.216-.535A.73.73 0 0 1 4.25 19h15.5q.32 0 .535.215a.73.73 0 0 1 .215.535.73.73 0 0 1-.215.535.73.73 0 0 1-.535.215z"
                                        ></path>
                                    </svg>
                                    <label
                                        className={`absolute left-14 transition-all pointer-events-none ${
                                            returnFocused || returnInputValue
                                                ? "top-2 text-sm text-gray-600"
                                                : "top-1/2 -translate-y-1/2 text-base text-gray-700"
                                        }`}
                                    >
                                        Return
                                    </label>
                                    <input
                                        type="text"
                                        value={returnInputValue}
                                        onChange={(e) =>
                                            handleReturnInputChange(
                                                e.target.value
                                            )
                                        }
                                        onFocus={() => setReturnFocused(true)}
                                        onBlur={() => setReturnFocused(false)}
                                        className={`w-full h-full pl-11 pr-3 ${
                                            returnFocused || returnInputValue
                                                ? "pt-6 pb-2"
                                                : ""
                                        } border border-[#807e7e] rounded-lg text-sas-text focus:outline-none focus:border-sas-blue hover:bg-gray-50 hover:border-gray-600 transition-colors`}
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Month selector */}
                <div className="bg-white px-8 -my-2">
                    <div className="flex flex-col gap-3">
                        {/* Month abbreviations row */}
                        <div className="relative py-2 -mx-2 overflow-hidden">
                            <div className="flex justify-between px-8 relative">
                                {/* Background highlight for ACTIVE selected months */}
                                {currentMonthIndex >= 0 && (
                                    <div
                                        className="absolute top-0 bottom-0 bg-gray-100 rounded-full transition-all duration-200"
                                        style={{
                                            left: `calc(${
                                                ((currentMonthIndex - 0.5) /
                                                    13) *
                                                100
                                            }% + ${100 / 26}%)`,
                                            width: `${(3 / 13) * 100}%`,
                                        }}
                                    />
                                )}
                                {/* Background highlight for HOVER */}
                                {hoveredMonthIndex !== null && (
                                    <div
                                        className="absolute top-0 bottom-0 bg-gray-200 rounded-full transition-all duration-200"
                                        style={{
                                            left: `calc(${
                                                ((hoveredMonthIndex - 1.5) /
                                                    13) *
                                                100
                                            }% + ${100 / 26}%)`,
                                            width: `${(3 / 13) * 100}%`,
                                        }}
                                    />
                                )}
                                {monthsData.map((month, index) => {
                                    // Center the selection - select the month before, current, and after
                                    const isInActiveRange =
                                        index >= currentMonthIndex &&
                                        index < currentMonthIndex + 3;

                                    return (
                                        <button
                                            key={index}
                                            onClick={() => {
                                                // When clicking the first month, select months 0-2
                                                if (index === 0) {
                                                    setCurrentMonthIndex(0);
                                                }
                                                // When clicking the last month, select months 10-12
                                                else if (index === 12) {
                                                    setCurrentMonthIndex(10);
                                                }
                                                // For middle months, center the selection
                                                else if (
                                                    index >= 1 &&
                                                    index <= 11
                                                ) {
                                                    setCurrentMonthIndex(
                                                        index - 1
                                                    );
                                                }
                                            }}
                                            onMouseEnter={() => {
                                                if (index === 0) {
                                                    setHoveredMonthIndex(1); // Show hover for months 0-2
                                                } else if (index === 12) {
                                                    setHoveredMonthIndex(11); // Show hover for months 10-12
                                                } else {
                                                    setHoveredMonthIndex(index);
                                                }
                                            }}
                                            onMouseLeave={() =>
                                                setHoveredMonthIndex(null)
                                            }
                                            className={`relative z-10 px-2 py-1 text-xs transition-colors ${
                                                isInActiveRange
                                                    ? "text-gray-900"
                                                    : "text-gray-600 hover:text-gray-800 cursor-pointer"
                                            }`}
                                        >
                                            {month.abbr}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Navigation arrows positioned below month selector */}
                <div className="bg-white">
                    <div className="px-6 pb-4 mt-2">
                        <div className="flex justify-between">
                            <button
                                onClick={() => {
                                    setSlideDirection("right");
                                    setCurrentMonthIndex(
                                        Math.max(0, currentMonthIndex - 1)
                                    );
                                    setTimeout(
                                        () => setSlideDirection(null),
                                        300
                                    );
                                }}
                                disabled={currentMonthIndex === 0}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                    currentMonthIndex === 0
                                        ? "bg-sas-blue hover:bg-sas-blue-dark opacity-50"
                                        : "bg-sas-blue hover:bg-sas-blue-dark"
                                }`}
                            >
                                <svg
                                    className="w-3 h-3 text-white"
                                    fill="none"
                                    strokeWidth="3"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M15.75 19.5L8.25 12l7.5-7.5"
                                    />
                                </svg>
                            </button>

                            <button
                                onClick={() => {
                                    setSlideDirection("left");
                                    setCurrentMonthIndex(
                                        Math.min(10, currentMonthIndex + 1)
                                    );
                                    setTimeout(
                                        () => setSlideDirection(null),
                                        300
                                    );
                                }}
                                disabled={currentMonthIndex >= 10}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                                    currentMonthIndex >= 10
                                        ? "bg-sas-blue hover:bg-sas-blue-dark opacity-50"
                                        : "bg-sas-blue hover:bg-sas-blue-dark"
                                }`}
                            >
                                <svg
                                    className="w-3 h-3 text-white"
                                    fill="none"
                                    strokeWidth="3"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        d="M8.25 4.5l7.5 7.5-7.5 7.5"
                                    />
                                </svg>
                            </button>
                        </div>
                    </div>
                </div>

                {/* Calendars */}
                <div
                    className="overflow-y-auto px-6 -mt-4"
                    style={{ height: "443px" }}
                >
                    <div className="grid grid-cols-3 gap-8 transition-all duration-300 ease-in-out pb-8">
                        {[0, 1, 2].map((offset) => {
                            const month = getMonthYear(offset);
                            const currentYear = new Date().getFullYear();
                            const monthYear = month.getFullYear();
                            const monthName = month.toLocaleDateString(
                                "en-US",
                                { month: "long" }
                            );
                            const displayName =
                                monthYear !== currentYear
                                    ? `${monthName} ${monthYear}`
                                    : monthName;

                            return (
                                <div
                                    key={`${currentMonthIndex}-${offset}`}
                                    className={
                                        slideDirection
                                            ? `animate-slide${
                                                  slideDirection === "left"
                                                      ? "Left"
                                                      : "Right"
                                              }`
                                            : ""
                                    }
                                >
                                    <h3 className="text-center text-base font-semibold mb-4 text-gray-900">
                                        {displayName}
                                    </h3>
                                    <div className="grid grid-cols-7 gap-1 mb-2">
                                        {[
                                            "M",
                                            "T",
                                            "W",
                                            "T",
                                            "F",
                                            "S",
                                            "S",
                                        ].map((day, index) => (
                                            <div
                                                key={index}
                                                className="text-center text-sm font-medium text-gray-500 h-8"
                                            >
                                                {day}
                                            </div>
                                        ))}
                                    </div>
                                    <div className="grid grid-cols-7 gap-1">
                                        {renderCalendar(month)}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Footer */}
                <div className="bg-white px-0 py-4 border-t border-gray-200 mt-6 mx-6">
                    <div className="flex items-center justify-between">
                        <label className="flex items-center gap-2 cursor-pointer px-2 py-2 rounded-lg focus-within:ring-2 focus-within:ring-sas-blue">
                            <input
                                type="checkbox"
                                checked={payWithPoints}
                                onChange={(e) =>
                                    onPayWithPointsChange(e.target.checked)
                                }
                                className="w-4 h-4 border border-gray-300 rounded focus:ring-0 focus:ring-offset-0 text-black accent-black"
                            />
                            <span className="text-sas-text text-base">
                                Pay with points
                            </span>
                        </label>

                        <div className="flex items-center gap-4">
                            {calculateTripLength() && (
                                <span className="text-gray-600">
                                    Trip length: {calculateTripLength()}
                                </span>
                            )}
                            <button
                                onClick={handleDone}
                                className="px-8 py-3 rounded-full font-semibold transition-colors bg-sas-blue text-white hover:bg-sas-blue-dark"
                            >
                                Done
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </div>,
        document.body
    );
};

export default DatePicker;
